import { useState, useEffect, useRef } from 'react';
import Select from 'react-select/creatable';
import { closeModal } from '@components/modal';
import { toast } from 'react-toastify';
import translate from '@data/translations';
import {
    PosItemModel,
    SKUType,
    SelectOptionAndValue,
    StockKeepingUnitModel,
    TaxModel,
    UnitOfMeasure,
    PaginatedData,
    SKUTypeName,
    SkuPluRateForForm,
    SKUStorageRole,
} from '@common/modelDefinition';
import {
    checkIfBarcodeOrCodeIsUnique,
    getSKUItems,
    createOrEditSkuItemAndSkuPluRate,
    createOrUpdatePosItemAndSkuPluRate,
    getSkuPluRate,
} from '../api';
import { StockController } from '../stockController';
import { renderFirstAvailablePlu } from '@pages/priceList/pos/posItem/form';
import useStore from '@data/state/zustand';
import ConfirmButtons from '@components/buttons/confirmButtons';
import CustomInput from '@components/custom/input';
import { ReactComponent as Search } from '../../../assets/custom/icons/search.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/custom/icons/trash.svg';

const ctrl = new StockController();

let searchDebouncer: any = null;

export const unitOfMeasure: UnitOfMeasure = {
    piece: 'Piece',
    kg: 'kg (kilogram)',
    l: 'L (liter)',
    m: 'm (meter)',
    kWh: 'kWh (kilowatt-hour)',
};

const skuPluRateTemplate: Partial<SkuPluRateForForm> = {
    id: undefined,
    skuMeasureCode: '',
    pluMeasureCode: '',
    multiplier: 1,
    posItemId: null,
    description: null,
    semiFinishedSkuId: null,
    StockKeepingUnit: null,
    SemiFinishedSKU: null,
    code: '',
    edit: false,
    delete: false,
    avgPurchasePrice: 0,
};

var emptyFormTemplate: StockKeepingUnitModel = {
    unitType: SKUType.beverage,
    barcode: null,
    code: '',
    name: '',
    description: '',
    category: null,
    salesPrice: 0,
    grossMarginPercentage: '0',
    calculateSalesPriceOnStockIncrease: false,
    active: true,
    taxId1: null,
    taxId2: null,
    taxId3: null,
    stockMeasureUnit: 'piece',
    purchaseMeasureUnit: null,
    purchaseMeasureUnit_stockUnitMultiplier: 1,
    minimumStock: 0,
    replenishStock: 0,
    isCounter: false,
    cumulativeLoss: 0,
    params: null,
    isSemiFinished: false,
    requiresProduction: false,
    avgPurchasePrice: 0,
    storageRole: SKUStorageRole.BAR,
    participatesInRetailPricing: true,
};

interface StockKeepingUnitsFormProps {
    skuItemId: number | null | undefined;
    reload: boolean;
    setReload: Function;
    closeFunc?: Function;
    isModalBig?: boolean;
    semiFinishedCallback?: Function;
}

const StockKeepingUnitsForm = (props: StockKeepingUnitsFormProps) => {
    const { skuItemId, reload, setReload } = props;
    const [data, setData] = useState({ ...emptyFormTemplate } as StockKeepingUnitModel);
    const [skuItems, setSkuItems] = useState([] as StockKeepingUnitModel[]);
    const [categoryValues, setCategoryValues] = useState([] as SelectOptionAndValue[] | null);
    const [categoryOptions, setCategoryOptions] = useState([] as SelectOptionAndValue[]);
    // const [parentSkuItemOptions, setParentSkuItemOptions] = useState([] as SelectOptionAndValue[]);
    const [isCheckboxActive, setIsCheckboxActive] = useState(false);
    const [skuPluRates, setSkuPluRates] = useState([] as SkuPluRateForForm[]);
    const [searchTermNormative, setSearchTerm] = useState('');
    const [skuItemsFromSearchBox, setSkuItemsFromSearchBox] = useState([] as StockKeepingUnitModel[]);
    const [totalNormativePrice, setTotalNormativePrice] = useState(0 as number);

    const name = useRef(null);
    const code = useRef(null);
    const tax = useRef(null);

    const taxes: TaxModel[] = useStore((appState: any) => appState.model.Tax);
    const invoicingSettings = useStore((appState: any) => appState.invoicingSettings);
    const fiscalGeneralSettings = useStore((appState: any) => appState.fiscal.fiscalGeneralSettings);

    const country = fiscalGeneralSettings && fiscalGeneralSettings.country ? fiscalGeneralSettings.country : '';
    const generalTax = invoicingSettings?.generalTax;

    useEffect(() => {
        const loadData = async () => {
            const { data: _skuItems }: PaginatedData = await getSKUItems(0, 1000, true);
            setSkuItems([..._skuItems]);

            let _data = { ...data };
            if (skuItemId) {
                //editing
                loadFormData(skuItemId, _skuItems);
            } else {
                _data.taxId1 = generalTax ? generalTax : null;
                setData(_data);
                ctrl.checkValidationInputs(_data, getValidationInputs);
            }

            const _skuItemTags: string[] = [];
            _skuItems.forEach((sku: StockKeepingUnitModel) => {
                if (sku.category) {
                    sku.category.split(';').forEach((o) => {
                        if (!_skuItemTags.includes(o)) {
                            _skuItemTags.push(o);
                        }
                    });
                }
            });
            const _tagsOptions: SelectOptionAndValue[] = [];
            _skuItemTags.forEach((t) => {
                const obj: SelectOptionAndValue = {
                    value: t,
                    label: t,
                };
                _tagsOptions.push(obj);
            });
            setCategoryOptions(_tagsOptions);
        };

        loadData().catch((err) => {
            console.log(err);
        });
        // eslint-disable-next-line
    }, [skuItemId]);

    const loadFormData = async (skuItemId: number | null | undefined, _skuItems = skuItems) => {
        if (_skuItems && skuItemId !== undefined && skuItemId !== null && !Number.isNaN(skuItemId)) {
            const currentData = _skuItems.find((t: StockKeepingUnitModel) => t.id === Number(skuItemId));
            const _category = currentData?.category ? currentData.category.split(';') : [];
            const _tagsValue: SelectOptionAndValue[] = [];
            _category.forEach((t: string) => {
                const obj: SelectOptionAndValue = {
                    value: t,
                    label: t,
                };
                _tagsValue.push(obj);
            });
            setCategoryValues(_tagsValue);

            if (currentData?.id) {
                setData(currentData);
                if (currentData.purchaseMeasureUnit) {
                    setIsCheckboxActive(true);
                } else {
                    setIsCheckboxActive(false);
                }

                //različito jer prvi put ide kontra - ako je poluproizvod prikazi veci modal
                updateModalClass(!currentData?.isSemiFinished);

                const _skuPluRates = await getSkuPluRate(currentData?.id, null, true);
                let totalNormativePrice = 0;

                const preparedSkuPluRates = _skuPluRates
                    ?.map((r) => {
                        if (r) {
                            const itemPrice =
                                r.SemiFinishedSKU?.storageRole === SKUStorageRole.KITCHEN
                                    ? r.avgPurchasePrice
                                    : r?.SemiFinishedSKU?.salesPrice;
                            const normativeRetailPrice = Number(r.multiplier) * Number(itemPrice || 0);
                            const withCode: SkuPluRateForForm = {
                                ...r,
                                code: r.SemiFinishedSKU?.code || '',
                                normativeRetailPrice: normativeRetailPrice,
                                edit: false,
                                avgPurchasePrice: r.avgPurchasePrice || 0,
                            };
                            totalNormativePrice = Number(totalNormativePrice) + Number(normativeRetailPrice);
                            return withCode;
                        } else {
                            return null;
                        }
                    })
                    .filter((r: any | null) => r !== null) as SkuPluRateForForm[];
                setSkuPluRates([...preparedSkuPluRates]);
                setTotalNormativePrice(totalNormativePrice);
            }
            ctrl.checkValidationInputs(currentData, getValidationInputs);
        }
    };

    const handleInputChange = (e: any) => {
        let _data: any = { ...data };
        if (e.target.type === 'text') {
            _data[e.target.name] = e.target.value;
            setData(_data);
        } else if (e.target.type === 'number') {
            _data[e.target.name] = parseFloat(e.target.value);
            setData(_data);
        }
        if (e.target.name === 'name' || e.target.name === 'code') {
            ctrl.checkValidationInputs(_data, getValidationInputs);
        }
    };

    const handleSelectChange = (e: any) => {
        let _data: any = { ...data };
        _data[e.target.name] = e.target.name === 'unitType' ? Number(e.target.value) : e.target.value;
        if (e.target.name === 'taxId1' || e.target.name === 'taxId2' || e.target.name === 'taxId3') {
            if (isNaN(parseFloat(e.target.value))) {
                _data[e.target.name] = null;
            }
        }

        if (e.target.name === 'stockMeasureUnit' && e.target.value === 'piece') {
            _data.purchaseMeasureUnit = 'piece';
            _data.purchaseMeasureUnit_stockUnitMultiplier = 1;
            setIsCheckboxActive(false);
        } else if (e.target.name === 'stockMeasureUnit' && e.target.value !== 'piece') {
            _data.purchaseMeasureUnit = 'piece';
            _data.purchaseMeasureUnit_stockUnitMultiplier = 1;
            setIsCheckboxActive(true);
        }

        if (e.target.name === 'storageRole') {
            if (e.target.value === SKUStorageRole.KITCHEN) {
                _data.participatesInRetailPricing = false;
            } else {
                _data.participatesInRetailPricing = true;
            }
        }

        setData(_data);
    };

    const handleCategoryChange = (_category: any) => {
        let category: string[] = [];
        let categoryParsed: string | null = null;
        if (_category !== null) {
            _category.forEach((t: SelectOptionAndValue) => {
                category.push(t.value);
            });
            categoryParsed = category.join(';');
        } else {
            categoryParsed = null;
        }
        let _data = { ...data };
        _data['category'] = categoryParsed;
        setCategoryValues(_category);
        setData(_data);
    };

    const handleIsActiveOrIsCounter = (name: string) => {
        let _data = { ...data };
        if (name === 'active') {
            _data['active'] = !_data.active;
        } else if (name === 'isCounter') {
            _data['isCounter'] = !_data.isCounter;
            if (_data.isCounter) {
                _data.purchaseMeasureUnit = null;
                _data.purchaseMeasureUnit_stockUnitMultiplier = null;
                _data.minimumStock = 0;
                _data.replenishStock = 0;
                setIsCheckboxActive(false);
            }
        }
        setData(_data);
    };

    const updateModalClass = (isSemiFinished?: boolean) => {
        if (isSemiFinished) {
            const elements = document.getElementsByClassName('modal-dialog modal-xl');
            for (let i = 0; i < elements.length; i++) {
                elements[i].className = 'modal-dialog modal-md';
            }
        } else {
            const elements = document.getElementsByClassName('modal-dialog modal-md');
            for (let i = 0; i < elements.length; i++) {
                elements[i].className = 'modal-dialog modal-xl';
            }
        }
    };

    const handleIsSemiFinishedOrRequiresProduction = (name: string) => {
        let _data = { ...data };
        if (name === 'isSemiFinished') {
            _data['isSemiFinished'] = !_data.isSemiFinished;
            if (!_data.isSemiFinished) {
                _data['requiresProduction'] = false;
            }
        } else if (name === 'requiresProduction') {
            _data['requiresProduction'] = !_data.requiresProduction;
        }
        setData(_data);
        if (name === 'isSemiFinished') {
            updateModalClass(!_data.isSemiFinished);
            if (!_data.isSemiFinished) {
                let _skuPluRates = skuPluRates?.map((r) => {
                    let _item = { ...r };
                    _item.delete = true;
                    return _item;
                });
                setSkuPluRates([..._skuPluRates]);
                setTotalNormativePrice(0);
            }
        }
    };

    const handlePurchase = () => {
        let _data = { ...data };
        if (!isCheckboxActive) {
            _data.purchaseMeasureUnit = 'piece';
            _data.purchaseMeasureUnit_stockUnitMultiplier = 1;
        } else {
            _data.purchaseMeasureUnit = null;
            _data.purchaseMeasureUnit_stockUnitMultiplier = null;
        }
        setData(_data);
        setIsCheckboxActive(!isCheckboxActive);
    };

    const getValidationInputs = (_data: StockKeepingUnitModel) => {
        const invalidInputs = [],
            validInputs = [];
        if (_data.name === null || _data.name?.trim() === '') {
            invalidInputs.push(name);
        } else {
            validInputs.push(name);
        }
        if (_data.code === null || _data.code?.trim() === '') {
            invalidInputs.push(code);
        } else {
            validInputs.push(code);
        }
        return { invalidInputs, validInputs };
    };

    const saveFormData = async (createPosItem?: boolean) => {
        try {
            let _skuItem: StockKeepingUnitModel = { ...data };
            _skuItem.barcode = _skuItem.barcode === '' ? null : _skuItem.barcode;

            let barcodeRepeats = 0,
                barcodeValid: string | boolean = true,
                codeRepeats = 0; //for null
            if (_skuItem.barcode !== null && _skuItem.barcode !== '') {
                barcodeRepeats = await checkIfBarcodeOrCodeIsUnique(_skuItem.barcode, null, _skuItem.id);
                barcodeValid = isValidBarcode(_skuItem.barcode);
            }

            if (_skuItem.code !== null && _skuItem.code !== '') {
                codeRepeats = await checkIfBarcodeOrCodeIsUnique(null, _skuItem.code, _skuItem.id);
            }
            if (!_skuItem.salesPrice) {
                _skuItem.salesPrice = 0;
                setData(_skuItem);
            }
            if (barcodeRepeats > 0) {
                throw new Error(translate('Barcode already exists!'));
            } else if (!barcodeValid) {
                throw new Error(translate('Barcode is not valid!'));
            } else if (codeRepeats > 0) {
                throw new Error(translate('Code already exists!'));
            } else if (_skuItem.code === '') {
                throw new Error(translate('Code is not valid!'));
            } else if (
                _skuItem?.minimumStock &&
                _skuItem?.replenishStock &&
                Number(_skuItem.replenishStock) < Number(_skuItem.minimumStock)
            ) {
                throw new Error(translate('Replenish stock must be greater or equal minimum stock!'));
            } else {
                if (barcodeValid) {
                    _skuItem.barcode = typeof barcodeValid === 'string' ? barcodeValid : null;
                }
                let validationResults = getValidationInputs(_skuItem);
                ctrl.markInvalidInputRefs(validationResults);
                if (validationResults.invalidInputs.length === 0) {
                    const skuItms = await createOrEditSkuItemAndSkuPluRate(_skuItem, skuPluRates);
                    const skuItm = skuItms ? skuItms[0] : null;
                    if (skuItm?.id && skuItm?.taxId1 && createPosItem) {
                        const _plu = await renderFirstAvailablePlu();
                        const newPosItem: PosItemModel = {
                            plu: _plu,
                            name: skuItm.name,
                            enabled: true,
                            unitMeasure: skuItm.stockMeasureUnit,
                            price: skuItm.salesPrice,
                            taxId: skuItm.taxId1,
                            tax2Id: skuItm.taxId2,
                            tax3Id: skuItm.taxId3,
                            isComboItem: false,
                            preparationRequired: false,
                            type: 'posTerminal',
                            subtype: SKUTypeName[skuItm.unitType as keyof typeof SKUTypeName],
                            category: skuItm.category,
                        };
                        const skuPluRate: any = {
                            skuId: skuItm.id,
                            skuMeasureCode: skuItm.stockMeasureUnit,
                            pluMeasureCode: skuItm.stockMeasureUnit,
                            multiplier: 1,
                            edit: true,
                        };
                        await createOrUpdatePosItemAndSkuPluRate(newPosItem, [skuPluRate]);
                    }
                    setData({ ...emptyFormTemplate });
                    setReload(!reload);
                    closeModal();
                }
            }
        } catch (err: any) {
            toast(err.message, {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: toast.TYPE.ERROR,
            });
        }
    };

    const onSearchSkuItem = async (e: any) => {
        clearTimeout(searchDebouncer);
        const _searchTerm = e.target.value;
        setSearchTerm(_searchTerm);
        searchDebouncer = setTimeout(async () => {
            const _itemsFromSearchBox =
                _searchTerm !== ''
                    ? skuItems?.filter(
                          (s) =>
                              s.name.toLowerCase().includes(_searchTerm.toLowerCase()) ||
                              s.code.toLowerCase().includes(_searchTerm.toLowerCase()) ||
                              s.barcode?.toLowerCase().includes(_searchTerm.toLowerCase())
                      )
                    : [];
            const filteredItems = _itemsFromSearchBox?.filter((i) => !i.parentId);
            setSkuItemsFromSearchBox(filteredItems);
        }, 400);
    };

    const handleRateChange = (
        skuItem: StockKeepingUnitModel | null,
        action: string,
        value?: number | string | null,
        skuPluRateIndex?: number
    ) => {
        let _skuPluRates: any[] = [...skuPluRates];
        let _newSkuPluRate: any = { ...skuPluRateTemplate };
        let _data = { ...data };
        if (skuItem?.id && action === 'add') {
            if (skuItem.id && !_skuPluRates.includes(skuItem.id)) {
                const itm = skuItems?.find((s) => s.id === skuItem?.id);
                _newSkuPluRate.skuId = Number(data.id);
                _newSkuPluRate.semiFinishedSkuId = Number(skuItem?.id);
                _newSkuPluRate.posItemId = null;
                _newSkuPluRate.skuMeasureCode = skuItem.stockMeasureUnit;
                _newSkuPluRate.pluMeasureCode = skuItem.stockMeasureUnit;
                _newSkuPluRate.StockKeepingUnit = data;
                _newSkuPluRate.SemiFinishedSKU = skuItem;
                _newSkuPluRate.code = skuItem.code;
                _newSkuPluRate.edit = true;
                _newSkuPluRate.avgPurchasePrice = itm?.avgPurchasePrice;
                _skuPluRates.push(_newSkuPluRate);
            }
        } else if ((skuPluRateIndex || skuPluRateIndex === 0) && action === 'multiplier') {
            let _editSkuPluRate = _skuPluRates?.find((r, index) => index === skuPluRateIndex);
            if (_editSkuPluRate) {
                if (_editSkuPluRate?.SemiFinishedSKU?.isCounter) {
                    _editSkuPluRate.multiplier = 1;
                } else {
                    if (Number(value) && Number(value) > 0) {
                        _editSkuPluRate.multiplier = Number(value);
                    } else {
                        _editSkuPluRate.multiplier = 0;
                    }
                }
                _editSkuPluRate.edit = true;
            }

            _skuPluRates = _skuPluRates?.map((r, index) => {
                if (index === skuPluRateIndex) {
                    return { ..._editSkuPluRate };
                } else {
                    return { ...r };
                }
            });
        } else if ((skuPluRateIndex || skuPluRateIndex === 0) && action === 'delete') {
            let _editSkuPluRate = _skuPluRates?.find((r, index) => index === skuPluRateIndex);
            if (_editSkuPluRate) {
                _editSkuPluRate.delete = true;
            }
            _skuPluRates = _skuPluRates?.map((r, index) => {
                if (index === skuPluRateIndex) {
                    return { ..._editSkuPluRate };
                } else {
                    return { ...r };
                }
            });
        }

        const result = recalculateRetailPriceAndMargin(_data, _skuPluRates);
        setData({ ..._data });
        setSkuPluRates([...result.recalcSkuPluRate]);
        setTotalNormativePrice(result.normativeRetailPriceTotal);
        setSkuItemsFromSearchBox([]);
        setSearchTerm('');
    };

    const recalculateRetailPriceAndMargin = (_data: StockKeepingUnitModel, _skuPluRates = skuPluRates) => {
        let totalNormativePrice = 0;
        const editedSkuPluRate = _skuPluRates
            ?.map((r) => {
                if (r) {
                    const itemPrice =
                        r.SemiFinishedSKU?.storageRole === SKUStorageRole.KITCHEN
                            ? r.avgPurchasePrice
                            : r?.SemiFinishedSKU?.salesPrice;
                    const unitRetailPrice = Number(r.multiplier) * Number(itemPrice);
                    let _editedItem = { ...r };
                    _editedItem.normativeRetailPrice = unitRetailPrice;
                    if (!r?.delete) {
                        totalNormativePrice = Number(totalNormativePrice) + unitRetailPrice;
                    }
                    return _editedItem;
                } else {
                    return null;
                }
            })
            ?.filter((r) => r !== null);

        return {
            normativeRetailPriceTotal: Number(totalNormativePrice),
            recalcSkuPluRate: editedSkuPluRate as SkuPluRateForForm[],
        };
    };

    const isActive = data?.active ? (
        <i className="fa fa-lg mr-8p fa-check-square-o pointer" onClick={() => handleIsActiveOrIsCounter('active')} />
    ) : (
        <i className="fa fa-lg mr-8p fa-square-o pointer" onClick={() => handleIsActiveOrIsCounter('active')} />
    );

    const isPurchaseUnitLikeStockUnit = isCheckboxActive ? (
        <i className="fa fa-lg mr-8p fa-check-square-o pointer" onClick={handlePurchase} />
    ) : (
        <i className="fa fa-lg mr-8p fa-square-o pointer" onClick={handlePurchase} />
    );

    // const isCounter = data?.isCounter ? (
    //     <i
    //         className="fa fa-lg mr-8p fa-check-square-o pointer"
    //         onClick={() => handleIsActiveOrIsCounter('isCounter')}
    //     />
    // ) : (
    //     <i className="fa fa-lg mr-8p fa-square-o pointer" onClick={() => handleIsActiveOrIsCounter('isCounter')} />
    // );

    const isSemiFinished = data?.isSemiFinished ? (
        <i
            className="fa fa-lg mr-8p fa-check-square-o pointer"
            onClick={() => handleIsSemiFinishedOrRequiresProduction('isSemiFinished')}
        />
    ) : (
        <i
            className="fa fa-lg mr-8p fa-square-o pointer"
            onClick={() => handleIsSemiFinishedOrRequiresProduction('isSemiFinished')}
        />
    );

    const requiresProduction = data?.requiresProduction ? (
        <i
            className="fa fa-lg mr-8p fa-check-square-o pointer"
            onClick={() => handleIsSemiFinishedOrRequiresProduction('requiresProduction')}
        />
    ) : (
        <i
            className="fa fa-lg mr-8p fa-square-o pointer"
            onClick={() => handleIsSemiFinishedOrRequiresProduction('requiresProduction')}
        />
    );

    // let parentSkuItemValues: SelectOptionAndValue[] = [];
    // const parentSkuItem = data?.parentId ? skuItems?.find((i) => i.id === data?.parentId) : null;
    // if (parentSkuItem?.id) {
    //     parentSkuItemValues = [
    //         {
    //             value: parentSkuItem?.id?.toString(),
    //             label: parentSkuItem?.name,
    //         },
    //     ];
    // }

    const salesPriceDiv = (
        <div className="ml-4p flex">
            <label className="text-bold ml-4p" htmlFor="">
                {translate('Sales price')}
            </label>
            <input
                disabled={data?.id ? true : false}
                autoComplete="off"
                name="salesPrice"
                placeholder={translate('Sales price')}
                type="number"
                className="input input-modal"
                value={data?.salesPrice || data.salesPrice === 0 ? parseFloat(Number(data.salesPrice)?.toFixed(2)) : ''}
                onChange={(e: any) => handleInputChange(e)}
            />
        </div>
    );

    return (
        <div className="">
            <div className="display-flex flex-wrap">
                <div className="flex mt-10p mr-8p">
                    <div className="display-flex">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Name')} <span className="color-secondary">({translate('required')})</span>
                            </label>
                            <input
                                ref={name}
                                autoComplete="off"
                                name="name"
                                placeholder={translate('Name')}
                                type="text"
                                className="input input-modal"
                                value={data.name ? data.name : ''}
                                onChange={(e: any) => handleInputChange(e)}
                            />
                        </div>
                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Type')}
                            </label>
                            <select
                                name="unitType"
                                autoComplete="off"
                                className="input input-modal"
                                value={data.unitType ? data.unitType : ''}
                                onChange={(e: any) => handleSelectChange(e)}
                            >
                                {Object.keys(SKUType).map((key: string) => {
                                    if (Number(key)) {
                                        return null;
                                    } else {
                                        return (
                                            <option
                                                key={SKUType[key as keyof typeof SKUType]}
                                                value={SKUType[key as keyof typeof SKUType]}
                                            >
                                                {translate(key)}
                                            </option>
                                        );
                                    }
                                })}
                            </select>
                        </div>
                    </div>

                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Code')} <span className="color-secondary">({translate('required')})</span>
                            </label>
                            <input
                                ref={code}
                                autoComplete="off"
                                name="code"
                                placeholder={translate('Code')}
                                type="text"
                                className="input input-modal"
                                value={data.code ? data.code : ''}
                                onChange={(e: any) => handleInputChange(e)}
                            />
                        </div>

                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Barcode')}
                            </label>
                            <input
                                autoComplete="off"
                                name="barcode"
                                placeholder={translate('Barcode')}
                                type="text"
                                className="input input-modal"
                                value={data.barcode ? data.barcode : ''}
                                onChange={(e: any) => handleInputChange(e)}
                            />
                        </div>
                    </div>
                    <div className="mt-12p">
                        <label className="text-bold mr-4p" htmlFor="">
                            {translate('Storage role')}
                        </label>
                        <select
                            name="storageRole"
                            autoComplete="off"
                            className="input input-modal"
                            value={data.storageRole ? data.storageRole : ''}
                            onChange={(e: any) => handleSelectChange(e)}
                        >
                            {Object.keys(SKUStorageRole).map((key: string) => {
                                if (Number(key)) {
                                    return null;
                                } else {
                                    return (
                                        <option
                                            key={SKUStorageRole[key as keyof typeof SKUStorageRole]}
                                            value={SKUStorageRole[key as keyof typeof SKUStorageRole]}
                                        >
                                            {translate(key)}
                                        </option>
                                    );
                                }
                            })}
                        </select>
                    </div>
                    <div className="mt-20p display-flex">
                        {data?.participatesInRetailPricing ? (
                            <i
                                className="fa fa-lg mr-8p fa-check-square-o pointer"
                                onClick={() => {
                                    let _data = { ...data };
                                    _data.participatesInRetailPricing = !_data.participatesInRetailPricing;
                                    setData({ ..._data });
                                }}
                            />
                        ) : (
                            <i
                                className="fa fa-lg mr-8p fa-square-o pointer"
                                onClick={() => {
                                    let _data = { ...data };
                                    _data.participatesInRetailPricing = !_data.participatesInRetailPricing;
                                    setData({ ..._data });
                                }}
                            />
                        )}
                        <label className="text-bold ml-4p" htmlFor="">
                            {translate('Participate in retail pricing')}{' '}
                            <span className="ml-4p">
                                (
                                {translate(
                                    'If enabled, the item has a retail price and it is possible to change the price via calculation and price changelog.'
                                )}
                                )
                            </span>
                        </label>
                    </div>
                    <br />
                    <div className="display-flex mt-12p">
                        <div className="flex display-flex align-items-center mr-8p">
                            <div className="">{isSemiFinished}</div>
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Is semi-finished item')}
                            </label>
                        </div>
                        {data.isSemiFinished ? (
                            <div className="flex display-flex align-items-center ml-8p">
                                <div className="">{requiresProduction}</div>
                                <label className="text-bold ml-4p" htmlFor="">
                                    {translate('Requires Production document')}
                                </label>
                            </div>
                        ) : null}
                    </div>

                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold mr-4p" htmlFor="">
                                {translate('Description')}
                            </label>
                            <input
                                autoComplete="off"
                                name="description"
                                placeholder={translate('Description')}
                                type="text"
                                className="input input-modal"
                                value={data.description ? data.description : ''}
                                onChange={(e: any) => handleInputChange(e)}
                            />
                        </div>
                        {!data.isSemiFinished ? salesPriceDiv : null}
                    </div>

                    <div className="mt-12p">
                        <label className="text-bold ml-4p" htmlFor="">
                            {translate('Category')}
                        </label>
                        <div className="">
                            <Select
                                isMulti
                                placeholder={translate('Add category')}
                                options={categoryOptions}
                                onChange={(e: any) => handleCategoryChange(e)}
                                value={categoryValues}
                            ></Select>
                        </div>
                    </div>

                    {/* <div className="mt-12p display-flex align-items-center">
                        <div className="">{isCounter}</div>
                        <label className="text-bold ml-4p" htmlFor="">
                            {translate('Is counter')}
                        </label>
                    </div> */}

                    <div className="mt-12p">
                        <label className="text-bold ml-4p" htmlFor="">
                            {translate('Stock measure code')}
                        </label>
                        <select
                            name="stockMeasureUnit"
                            autoComplete="off"
                            className="input input-modal"
                            value={data.stockMeasureUnit ? data.stockMeasureUnit : ''}
                            onChange={(e: any) => handleSelectChange(e)}
                        >
                            {Object.keys(unitOfMeasure).map((key) => (
                                <option key={key} value={key}>
                                    {translate(unitOfMeasure[key])}
                                </option>
                            ))}
                        </select>
                    </div>

                    {data?.stockMeasureUnit === 'piece' || data?.isCounter ? null : (
                        <div className="mt-12p display-flex align-items-center">
                            <div className="">{isPurchaseUnitLikeStockUnit}</div>
                            <label className="text-bold ml-4p mr-8p" htmlFor="">
                                {translate('Purchase measure unit not equal stock measure code')}
                            </label>
                        </div>
                    )}

                    <div className="display-flex mt-12p">
                        {isCheckboxActive ? (
                            <div
                                className="mr-4p flex"
                                style={{ justifyContent: 'space-between', display: 'flex', flexDirection: 'column' }}
                            >
                                <label className="text-bold ml-4p" htmlFor="">
                                    {translate('Purchase measure unit')}
                                </label>
                                <input
                                    disabled={true}
                                    autoComplete="off"
                                    name="purchaseMeasureUnit"
                                    placeholder={translate('Purchase measure unit')}
                                    type="text"
                                    className="input input-modal"
                                    value={
                                        data.purchaseMeasureUnit
                                            ? translate(unitOfMeasure[data.purchaseMeasureUnit])
                                            : ''
                                    }
                                    onChange={() => {}}
                                />
                            </div>
                        ) : null}

                        {data.purchaseMeasureUnit && isCheckboxActive ? (
                            <div
                                className="ml-4p flex"
                                style={{ justifyContent: 'space-between', display: 'flex', flexDirection: 'column' }}
                            >
                                <label className="text-bold ml-4p" htmlFor="">
                                    {translate('Purchase unit to stock unit multiplier')}
                                </label>
                                <input
                                    autoComplete="off"
                                    name="purchaseMeasureUnit_stockUnitMultiplier"
                                    placeholder={translate('Purchase unit to stock unit multiplier')}
                                    type="number"
                                    className="input input-modal"
                                    value={
                                        data?.purchaseMeasureUnit_stockUnitMultiplier ||
                                        data.purchaseMeasureUnit_stockUnitMultiplier === 0
                                            ? parseFloat(
                                                  Number(data.purchaseMeasureUnit_stockUnitMultiplier)?.toFixed(2)
                                              )
                                            : ''
                                    }
                                    onChange={(e: any) => handleInputChange(e)}
                                />
                            </div>
                        ) : null}
                    </div>

                    {data?.storageRole === SKUStorageRole.KITCHEN ? (
                        <div className="mt-12p">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Cumulative loss in %')}
                            </label>
                            <input
                                autoComplete="off"
                                name="cumulativeLoss"
                                placeholder={translate('Cumulative loss in %')}
                                type="number"
                                className="input input-modal"
                                value={
                                    data?.cumulativeLoss || data.cumulativeLoss === 0
                                        ? parseFloat(Number(data.cumulativeLoss)?.toFixed(2))
                                        : ''
                                }
                                onChange={(e: any) => handleInputChange(e)}
                            />
                        </div>
                    ) : null}

                    <div className="display-flex mt-12p">
                        {data?.isCounter ? null : (
                            <div className="mr-4p flex">
                                <label className="text-bold ml-4p" htmlFor="">
                                    {translate('Minimum stock')}
                                </label>
                                <input
                                    autoComplete="off"
                                    name="minimumStock"
                                    placeholder={translate('Minimum stock')}
                                    type="number"
                                    className="input input-modal"
                                    value={
                                        data?.minimumStock || data.minimumStock === 0
                                            ? parseFloat(Number(data.minimumStock)?.toFixed(2))
                                            : ''
                                    }
                                    onChange={(e: any) => handleInputChange(e)}
                                />
                            </div>
                        )}
                        {data?.isCounter ? null : (
                            <div className="ml-4p flex">
                                <label className="text-bold ml-4p" htmlFor="">
                                    {translate('Replenish stock')}
                                </label>
                                <input
                                    autoComplete="off"
                                    name="replenishStock"
                                    placeholder={translate('Replenish stock')}
                                    type="number"
                                    className="input input-modal"
                                    value={
                                        data?.replenishStock || data.replenishStock === 0
                                            ? parseFloat(Number(data.replenishStock)?.toFixed(2))
                                            : ''
                                    }
                                    onChange={(e: any) => handleInputChange(e)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="display-flex mt-12p">
                        <div className="mr-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Tax')}:
                            </label>
                            <select
                                ref={tax}
                                name="taxId1"
                                className="input input-modal"
                                onChange={(e: any) => handleSelectChange(e)}
                                value={data.taxId1 ? data.taxId1 : ''}
                            >
                                <option>{translate('No tax')}</option>
                                {taxes?.map((t) => {
                                    if (t.isActive) {
                                        return t.id !== Number(data.taxId2) && t.id !== Number(data.taxId3) ? (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ) : null;
                                    }
                                    return null;
                                })}
                            </select>
                        </div>

                        <div className="mr-4p ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Tax 2')}:
                            </label>
                            <select
                                disabled={country === 'BIH' ? true : false}
                                name="taxId2"
                                className="input input-modal"
                                onChange={(e: any) => handleSelectChange(e)}
                                value={data.taxId2 ? data.taxId2 : ''}
                            >
                                <option>{translate('No tax')}</option>
                                {taxes?.map((t) => {
                                    if (t.isActive) {
                                        return t.id !== Number(data.taxId1) && t.id !== Number(data.taxId3) ? (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ) : null;
                                    }
                                    return null;
                                })}
                            </select>
                        </div>

                        <div className="ml-4p flex">
                            <label className="text-bold ml-4p" htmlFor="">
                                {translate('Tax 3')}:
                            </label>
                            <select
                                disabled={country === 'BIH' ? true : false}
                                name="taxId3"
                                className="input input-modal"
                                onChange={(e: any) => handleSelectChange(e)}
                                value={data.taxId3 ? data.taxId3 : ''}
                            >
                                <option>{translate('No tax')}</option>
                                {taxes?.map((t) => {
                                    if (t.isActive) {
                                        return t.id !== Number(data.taxId1) && t.id !== Number(data.taxId2) ? (
                                            <option key={t.id} value={t.id}>
                                                {t.name}
                                            </option>
                                        ) : null;
                                    }
                                    return null;
                                })}
                            </select>
                        </div>
                    </div>

                    {/* <div className="mt-12p">
                <label className="text-bold ml-4p" htmlFor="">
                    {translate('Parent stock keeping unit')}
                </label>
                <Select
                    isDisabled={!data?.id ? false : true}
                    placeholder={translate('Parent stock keeping unit')}
                    options={parentSkuItemOptions}
                    onChange={(change: any) => {
                        let _data = { ...data };
                        if (change) {
                            _data.parentId = Number(change.value) ? Number(change.value) : null;
                        } else {
                            _data.parentId = null;
                        }
                        setData({ ..._data });
                    }}
                    value={parentSkuItemValues}
                ></Select>
            </div> */}

                    <div className="mt-12p display-flex align-items-center">
                        <div className="">{isActive}</div>
                        <label className="text-bold ml-4p mr-8p" htmlFor="">
                            {translate('Active')}
                        </label>
                    </div>
                    <br />
                </div>

                {data.isSemiFinished ? (
                    <div className="flex ml-8p">
                        <div style={{ borderRadius: '8px', padding: '6px ' }}>
                            <div className="text-center mb-2">{translate('NORMATIVE')}</div>
                            <div className="mb-2">
                                <CustomInput
                                    onInputChange={(e: any) => {
                                        onSearchSkuItem(e);
                                    }}
                                    value={searchTermNormative}
                                    icon={<Search />}
                                    placeholder={translate('Sku item by name, code or barcode')}
                                    width={'300px'}
                                />
                                {skuItemsFromSearchBox?.length > 0 ? (
                                    <div className="options-wrapper" style={{ width: '300px' }}>
                                        {skuItemsFromSearchBox?.map((i, index) => {
                                            if (i?.active) {
                                                return (
                                                    <div
                                                        className="option"
                                                        style={{ width: '300px' }}
                                                        key={index}
                                                        onClick={(e) => {
                                                            handleRateChange(i, 'add');
                                                        }}
                                                    >
                                                        <span className="mx-2">{i.name}</span>
                                                        <span className="mx-2">{i.code}</span>
                                                        <span className="mx-2">
                                                            {translate(unitOfMeasure[i.stockMeasureUnit])}
                                                        </span>
                                                    </div>
                                                );
                                            } else {
                                                return null;
                                            }
                                        })}
                                    </div>
                                ) : null}
                            </div>

                            {skuPluRates?.length > 0 ? (
                                <div>
                                    <div className="sku-plu-rates-wrapper" style={{ maxHeight: '400px' }}>
                                        {skuPluRates?.map((r, index) => {
                                            if (!r.delete) {
                                                return (
                                                    <div className="sku-plu-rates" key={index}>
                                                        <div className="flex-start-between">
                                                            <div className="display-flex flex-column">
                                                                <div className="  text-bold">
                                                                    {r.SemiFinishedSKU?.name}
                                                                    <small className="text-normal">
                                                                        {' '}
                                                                        {r.SemiFinishedSKU?.stockMeasureUnit
                                                                            ? translate(
                                                                                  unitOfMeasure[
                                                                                      r.SemiFinishedSKU
                                                                                          ?.stockMeasureUnit as keyof typeof unitOfMeasure
                                                                                  ]
                                                                              )
                                                                            : null}
                                                                    </small>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div
                                                                    onClick={() => {
                                                                        handleRateChange(null, 'delete', null, index);
                                                                    }}
                                                                    className="button-white-w-icon-only fill-red text-right align-items-right"
                                                                >
                                                                    <DeleteIcon />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex-start-between mt-8p">
                                                            <div className="flex-center">
                                                                <label className="ml-4p mr-8p mt-4p">
                                                                    {translate('Multiplier')}:
                                                                </label>
                                                                <input
                                                                    className="bg-white input input-modal ml-4p"
                                                                    value={
                                                                        r?.multiplier || r?.multiplier === 0
                                                                            ? r.multiplier
                                                                            : ''
                                                                    }
                                                                    onChange={(e) =>
                                                                        handleRateChange(
                                                                            null,
                                                                            'multiplier',
                                                                            e.target.value,
                                                                            index
                                                                        )
                                                                    }
                                                                    name="multiplier"
                                                                    placeholder={translate('Multiplier')}
                                                                    autoComplete="off"
                                                                    type="number"
                                                                    min={0}
                                                                />{' '}
                                                            </div>
                                                            <div
                                                                className="list-item-text-right font-12p"
                                                                style={{ flex: 2 }}
                                                            >
                                                                {translate('MPC')}:{' '}
                                                                {r?.normativeRetailPrice
                                                                    ? Number(r.normativeRetailPrice)?.toFixed(4)
                                                                    : 0.0}{' '}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                return null;
                                            }
                                        })}
                                    </div>
                                    <div
                                        className="sku-plu-rates"
                                        style={{
                                            borderTop: '1px solid lightgrey',
                                            borderTopLeftRadius: 0,
                                            borderTopRightRadius: 0,
                                            paddingTop: '6px',
                                        }}
                                    >
                                        <div className="flex-center">
                                            <label className="text-bold mb-0">
                                                {translate('Total norm retail price')}:{' '}
                                            </label>
                                            <div style={{ flex: 2 }} className="list-item-text-right">
                                                {totalNormativePrice ? Number(totalNormativePrice)?.toFixed(4) : 0.0}
                                            </div>

                                            <div>
                                                {skuPluRates?.length > 0 &&
                                                !data?.id &&
                                                Number(data?.salesPrice) !== Number(totalNormativePrice) ? (
                                                    <div
                                                        className="button-primary"
                                                        style={{
                                                            flex: 'none',
                                                            paddingLeft: '5px',
                                                            paddingRight: '5px',
                                                        }}
                                                        onClick={() => {
                                                            if (!data?.id) {
                                                                let _data = { ...data };
                                                                _data.salesPrice = Number(totalNormativePrice);
                                                                setData({ ..._data });
                                                            }
                                                        }}
                                                    >
                                                        {translate('Apply price')}
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="">{translate('List is empty')}</div>
                            )}
                        </div>
                        <div className="display-flex mt-12p">
                            <div className="flex">{salesPriceDiv}</div>
                            <div className="flex"></div>
                        </div>
                    </div>
                ) : null}
            </div>

            <div className="flex-center-center mt-20p">
                <div data-dismiss="modal" onClick={async () => await saveFormData(true)} className="button-info mr-8p">
                    {translate('Save and create pos item')}
                </div>
                <ConfirmButtons
                    onConfirm={async () => await saveFormData()}
                    onCancel={() => {
                        if (props.closeFunc) {
                            props.closeFunc();
                        } else {
                            closeModal();
                        }
                    }}
                    confirmText={'Save'}
                />
            </div>
        </div>
    );
};

export const isValidBarcode = (eanCode: string) => {
    // Check if only digits
    const ValidChars = '0123456789';

    for (let i = 0; i < eanCode.length; i++) {
        const digit = eanCode.charAt(i);
        if (ValidChars.indexOf(digit) === -1) {
            return false;
        }
    }

    // Add five 0 if the code has only 8 digits
    if (eanCode.length === 8) {
        return false;
        // eanCode = '00000' + eanCode;
    }

    // Add even numbers together
    let even =
        Number(eanCode.charAt(1)) +
        Number(eanCode.charAt(3)) +
        Number(eanCode.charAt(5)) +
        Number(eanCode.charAt(7)) +
        Number(eanCode.charAt(9)) +
        Number(eanCode.charAt(11));

    // Multiply this result by 3
    even = even * 3;

    // Add odd numbers together
    let odd =
        Number(eanCode.charAt(0)) +
        Number(eanCode.charAt(2)) +
        Number(eanCode.charAt(4)) +
        Number(eanCode.charAt(6)) +
        Number(eanCode.charAt(8)) +
        Number(eanCode.charAt(10));

    // Add two totals together
    const total = even + odd;

    // Calculate the checksum
    // Divide total by 10 and store the remainder
    let checksum = total % 10;

    // If result is not 0 then take away 10
    if (checksum !== 0) {
        checksum = 10 - checksum;
    }

    if (eanCode.length === 12) {
        // Check for 12 digits
        eanCode = eanCode + checksum.toString();
        return eanCode;
    } else if (eanCode.length === 13) {
        // Check for 13 digits
        // Get the check number
        const originalCheck = Number(eanCode.substring(eanCode.length - 1));
        // Return the result
        if (checksum !== originalCheck) {
            return false;
        }
        return eanCode;
    } else {
        return false;
    }
};

export default StockKeepingUnitsForm;
